import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import './LeadSavedViews.css';

const FILTER_KEYS = [
  'search', 'accepted', 'replied', 'hasEmail', 'hasLinkedIn', 'listId', 'listTitle',
  'labels', 'labelMode', 'crmProvider', 'crmSyncStatus', 'crmOpenOpportunity',
  'crmDoNotContact', 'crmRecentActivityDays', 'campaignId', 'campaignFit',
  'fitMinScore', 'fitMaxScore', 'sortBy', 'sortDir', 'limit',
];

function LeadSavedViews({ onNotice, onError }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [views, setViews] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');

  const currentFilters = useMemo(() => {
    const result = {};
    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value != null && value !== '') result[key] = value;
    });
    return result;
  }, [searchParams]);

  const load = useCallback(async () => {
    try {
      const response = await api.get('/leads/workspace/views');
      setViews(response.data.views || []);
    } catch (error) {
      onError?.(error.response?.data?.message || error.response?.data?.error || 'Opgeslagen views konden niet worden geladen.');
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const applyView = (id) => {
    setSelectedId(id);
    const view = views.find((item) => item.id === id);
    if (!view) return;
    const next = new URLSearchParams();
    Object.entries(view.filters || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) next.set(key, value.join(','));
      else if (value != null && value !== '') next.set(key, String(value));
    });
    next.set('page', '1');
    setSearchParams(next);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const response = await api.post('/leads/workspace/views', { name: trimmed, filters: currentFilters });
      await load();
      setSelectedId(response.data.view?.id || '');
      setName('');
      setShowForm(false);
      onNotice?.(`View “${trimmed}” opgeslagen.`);
    } catch (error) {
      onError?.(error.response?.data?.message || error.response?.data?.error || 'View opslaan mislukt.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selectedId) return;
    const view = views.find((item) => item.id === selectedId);
    try {
      await api.delete(`/leads/workspace/views/${selectedId}`);
      setSelectedId('');
      await load();
      onNotice?.(`View “${view?.name || ''}” verwijderd.`);
    } catch (error) {
      onError?.(error.response?.data?.message || error.response?.data?.error || 'View verwijderen mislukt.');
    }
  };

  return <section className="lead-saved-views">
    <label>
      <span>Opgeslagen view</span>
      <select value={selectedId} onChange={(event) => applyView(event.target.value)}>
        <option value="">Huidige filters</option>
        {views.map((view) => <option key={view.id} value={view.id}>{view.name}</option>)}
      </select>
    </label>
    {!showForm && <button type="button" onClick={() => setShowForm(true)}>Huidige filters bewaren</button>}
    {showForm && <div className="lead-saved-views-form">
      <input autoFocus value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && save()} placeholder="Naam van deze view" />
      <button type="button" onClick={save} disabled={saving || !name.trim()}>Opslaan</button>
      <button type="button" className="secondary" onClick={() => { setShowForm(false); setName(''); }}>Annuleren</button>
    </div>}
    {selectedId && <button type="button" className="danger" onClick={remove}>View verwijderen</button>}
  </section>;
}

export default LeadSavedViews;
